/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Thu June 15 2021 14:30:00 PM
 *
 *******************************************************/
//flow
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { getShop } from 'src/helpers';
import { Icon, Link, TextStyle, Banner } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import { connect } from 'react-redux';
import { isThemeV3, isThemeTurbo, enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import { URL } from 'src/constants/links';
import { showChatbox } from 'src/integrations/chatbox';

type Props = {
  featureID: String,
  emailSupport: string
};

type ComposedProps = Props & WithI18nProps;

const WarningMetricIncompatible: React.FC<ComposedProps> = ({
  tenantApp,
  i18n,
  themeLive,
  marginTop
}) => {
  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
  const [isShow, setIsShow] = useState(false);
  const [isShowForFelibv1NoTracking, setIsShowForFelibv1NoTracking] = useState(false);
  const [isShowForFelibv1v2WithTracking, setIsShowForFelibv1v2WithTracking] = useState(false);

  useEffect(() => {
    if (!themeLive) {
      // do nothing
    } else if (isThemeV3(themeLive) || isThemeTurbo(themeLive)) {
      // do nothing
    } else if (
      themeLive.themeLibV2 ||
      _.get(tenantApp, 'adminAppFeatures.showRevenueTrackingV1', false)
    ) {
      setIsShowForFelibv1v2WithTracking(true);
    } else if (!_.get(tenantApp, 'adminAppFeatures.showRevenueTrackingV1', false)) {
      setIsShowForFelibv1NoTracking(true);
    }
  }, [isShowForFelibv1NoTracking, isShowForFelibv1v2WithTracking, themeLive, tenantApp]);

  useEffect(() => {
    try {
      const numOfTimesClose =
        localStorage.getItem(`${getShop()}-warning-analytic-incompatible-metric`) || 0;
      const lastCloseAt = localStorage.getItem(
        `${getShop()}-warning-analytic-incompatible-metric-at`
      );
      const isOver7Days = (Date.now() - lastCloseAt) / 1000 > 7 * 24 * 60 * 60;

      if (isOver7Days && +numOfTimesClose < 3) {
        setIsShow(true);
      } else {
        setIsShow(false);
      }
    } catch (error) {}
  }, [isShow]);

  const onDismiss = () => {
    try {
      const numOfTimesClose =
        localStorage.getItem(`${getShop()}-warning-analytic-incompatible-metric`) || 0;
      localStorage.setItem(
        `${getShop()}-warning-analytic-incompatible-metric`,
        +numOfTimesClose + 1
      );
      localStorage.setItem(`${getShop()}-warning-analytic-incompatible-metric-at`, Date.now());
      setIsShow(false);
    } catch (error) {}
  };

  const onOpenBeaconChat = () => {
    showChatbox();
  };

  return isShow && (isShowForFelibv1NoTracking || isShowForFelibv1v2WithTracking) ? (
    <div style={{ marginTop: marginTop || '20px' }}>
      <Banner
        status="warning"
        title={i18n.translate(
          'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_title'
        )}
        action={{
          content: i18n.translate(
            'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_btn'
          ),
          onAction: () =>
            history.push(
              isEnableShopifyIntegration
                ? routes.shopifyIntegration.view()
                : routes.themeSetup.view()
            )
        }}
        onDismiss={onDismiss}>
        <div>
          <p>
            {i18n.translate(
              'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_content',
              {
                conversionRate: (
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_content_conversionRate'
                    )}
                  </TextStyle>
                ),
                clickRate: (
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_content_clickRate'
                    )}
                  </TextStyle>
                ),
                searchWithoutClicks: (
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_content_searchWithoutClick'
                    )}
                  </TextStyle>
                ),
                theme: (
                  <span className="img-new-tab">
                    <Link
                      onClick={() =>
                        history.push(
                          isEnableShopifyIntegration
                            ? routes.shopifyIntegration.view()
                            : routes.themeSetup.view()
                        )
                      }>
                      <span style={{ color: '#2C6ECB' }}>
                        {i18n.translate(
                          'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_content_theme'
                        )}
                      </span>
                      <Icon source={ExternalMinor} color="interactive" />
                    </Link>
                  </span>
                )
              }
            )}
          </p>
          <br />
          <p>
            {i18n.translate(
              'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_content2',
              {
                guide: (
                  <span className="img-new-tab">
                    <a href={URL['Analytic_help_guide']} target="_blank" rel="noopener noreferrer">
                      <span style={{ color: '#2C6ECB' }}>
                        {i18n.translate(
                          'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_content2_guide'
                        )}
                      </span>
                      <Icon source={ExternalMinor} color="interactive" />
                    </a>
                  </span>
                ),
                contactUs: (
                  <span className="img-new-tab">
                    <Link onClick={onOpenBeaconChat}>
                      <span style={{ color: '#2C6ECB' }}>
                        {i18n.translate(
                          'AdminHTML.Analytics.DidYouKnow.didYouKnow_Incompatible_metric_content2_contact_us'
                        )}
                      </span>
                      <Icon source={ExternalMinor} color="interactive" />
                    </Link>
                  </span>
                )
              }
            )}
          </p>
        </div>
      </Banner>
    </div>
  ) : (
    <></>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({});

const mapStateToProps = (state, props) => {
  const themes = _.get(state, 'themeSetup.themes', []) || [];
  const themeLive = themes.find(theme => theme.published);
  return {
    themeLive,
    tenantApp: _.get(state.oauth, 'tenantApp', null)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(WarningMetricIncompatible));
